// Trie-based substring index for contact lookup.
//
// Each indexed string contributes ALL of its suffixes to the trie. That makes a
// plain prefix descent equivalent to substring search: descending by the chars
// of `query` lands on a node whose subtree-collected ids are exactly the
// entries containing `query` anywhere in their text.
//
// Lookup is O(|query|) plus the result-set size — independent of the corpus.

class TrieNode {
  children: Map<string, TrieNode> = new Map();
  ids: Set<number> = new Set();
}

export interface TrieEntry {
  id: number;
  text: string;
}

export class ContactTrie {
  private root = new TrieNode();

  insert(id: number, text: string): void {
    if (!text) return;
    const lower = text.toLowerCase();
    for (let start = 0; start < lower.length; start++) {
      let node = this.root;
      for (let i = start; i < lower.length; i++) {
        const ch = lower[i];
        let next = node.children.get(ch);
        if (!next) {
          next = new TrieNode();
          node.children.set(ch, next);
        }
        node = next;
        node.ids.add(id);
      }
    }
  }

  // Returns the set of ids whose indexed text contains `query` as a substring.
  // Returns null for empty query (caller should treat as "no filter").
  search(query: string): Set<number> | null {
    if (!query) return null;
    const lower = query.toLowerCase();
    let node = this.root;
    for (const ch of lower) {
      const next = node.children.get(ch);
      if (!next) return new Set();
      node = next;
    }
    return node.ids;
  }

  static from(entries: TrieEntry[]): ContactTrie {
    const trie = new ContactTrie();
    for (const entry of entries) trie.insert(entry.id, entry.text);
    return trie;
  }
}
